import ChartLayout from '../../models/chart_layout'
import Element from '../../models/element'
import PaneModel from '../../models/pane'
import { HLC, compareHlc, receiveHlc } from '../../utils/hlc'
import { AutoSaveChangeType, AutoSaveElement, DrawingChangeTimestamp, DrawingContainerType } from './constants'

export function getContainerType(model: PaneModel) {
  const indicatorElement = model.getAllElements().find((el) => el.isIndicator())
  if (indicatorElement) {
    return (indicatorElement.instance?.type ?? null) as DrawingContainerType
  }
  const chartElement = model.getAllElements().find((el) => el.isChart())
  if (chartElement) {
    return 'chart'
  }
  return null
}

export function getTicker(model: PaneModel) {
  return model.chart().ticker
}

export function updateInternalStoreWithChangedElement({
  element,
  changeType,
  currentInternalStore,
  lastUpdateTimestamp,
}: {
  element: Element
  changeType: AutoSaveChangeType
  currentInternalStore: AutoSaveElement[]
  lastUpdateTimestamp: HLC
}) {
  const paneModel = element.pane()
  const ticker = getTicker(paneModel)
  const containerType = getContainerType(paneModel)
  if (paneModel && containerType && (!element.destroyed || changeType === 'destroy')) {
    const instanceJson = element.instance.toAutosaveConfig()
    const newDrawing: AutoSaveElement = {
      elementId: element.elementId!,
      zIndex: element.zIndex,
      ticker,
      lastChange: lastUpdateTimestamp,
      changeType,
      containerType,
      elementAttrs: JSON.stringify(instanceJson),
    }

    const newInternalStore = [...currentInternalStore]
    const updateDrawingIndex = newInternalStore.findIndex(
      (drawingElement) => drawingElement.elementId === newDrawing.elementId
    )

    let shouldSyncAndSave = false
    if (updateDrawingIndex > -1) {
      const updatedRecord = newInternalStore[updateDrawingIndex]
      const hasChangedZIndex = updatedRecord.zIndex !== newDrawing.zIndex
      const hasChangedType = updatedRecord.changeType !== newDrawing.changeType
      const hasChangedAttrs = updatedRecord.elementAttrs !== newDrawing.elementAttrs
      const hasChange = hasChangedType || hasChangedAttrs || hasChangedZIndex
      if (updatedRecord.ticker === ticker && hasChange && updatedRecord.changeType !== 'destroy') {
        newInternalStore[updateDrawingIndex] = newDrawing
        shouldSyncAndSave = true
      }
    } else {
      newInternalStore.push(newDrawing)
      shouldSyncAndSave = true
    }

    return {
      newInternalStore,
      shouldSyncAndSave,
      newDrawing,
    }
  }
}

export function getTickersAndIndicatorsInLayoutModel(chartLayout: ChartLayout) {
  const tickers: string[] = []
  const containerTypes: Array<DrawingContainerType> = []
  chartLayout.getAllPanes().forEach((paneModel) => {
    const ticker = getTicker(paneModel)
    const containerType = getContainerType(paneModel)
    if (!tickers.includes(ticker)) {
      tickers.push(ticker)
    }
    if (!containerTypes.includes(containerType)) {
      containerTypes.push(containerType)
    }
  })
  return { tickers, containerTypes }
}

export function mergeUpdatedDrawingsToInternalStore({
  updatedDrawings,
  currentInternalStore,
  lastUpdateTimestamp,
}: {
  updatedDrawings: AutoSaveElement[]
  currentInternalStore: AutoSaveElement[]
  lastUpdateTimestamp: DrawingChangeTimestamp
}) {
  let newLastUpdateTimestamp = lastUpdateTimestamp
  const newInternalStore = [...currentInternalStore]
  const updatedElementIds: string[] = []

  updatedDrawings.forEach((drawing) => {
    newLastUpdateTimestamp = receiveHlc(newLastUpdateTimestamp, drawing.lastChange)
    const drawingIndex = newInternalStore.findIndex(({ elementId }) => drawing.elementId === elementId)

    if (drawingIndex === -1) {
      newInternalStore.push(drawing)
      updatedElementIds.push(drawing.elementId)
    } else if (compareHlc(newInternalStore[drawingIndex].lastChange, drawing.lastChange) < 0) {
      newInternalStore[drawingIndex] = drawing
      updatedElementIds.push(drawing.elementId)
    }
  })
  return { newLastUpdateTimestamp, newInternalStore, updatedElementIds }
}
